import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Analytics — response-time + outcome metrics derived from the tickets collection.
// All computed client-side from data the admin can already read (no new backend).
// Headline metric is time-to-accept: createdAt -> assignedTo.acceptedAt.

const RANGES = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: 'all', label: 'All time', days: null },
];

export default function Analytics() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const [tickets, setTickets] = useState(null);
  const [rangeKey, setRangeKey] = useState('30');

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(collection(db, 'vendors', vendorId, 'tickets'), orderBy('createdAt', 'desc')),
      (snap) => setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [vendorId]);

  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];

  const stats = useMemo(() => computeStats(tickets || [], range.days), [tickets, range.days]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Analytics</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={rangeKey === r.key ? 'btn-chip-active' : 'btn-chip'}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: -4 }}>
        Response times and outcomes across your dispatched tickets.
      </p>

      {tickets === null && <div className="placeholder">Loading…</div>}

      {tickets !== null && stats.total === 0 && (
        <div className="placeholder">No tickets in this period yet.</div>
      )}

      {tickets !== null && stats.total > 0 && (
        <>
          <div style={cardGrid}>
            <StatCard label="Tickets" value={stats.total} sub={`${stats.active} still active`} />
            <StatCard
              label="Median time to accept"
              value={fmtDuration(stats.medianAccept)}
              sub={stats.acceptedCount ? `across ${stats.acceptedCount} accepted` : 'no accepts yet'}
            />
            <StatCard
              label="Acceptance rate"
              value={stats.decided ? `${Math.round((stats.acceptedCount / stats.decided) * 100)}%` : '—'}
              sub={`${stats.acceptedCount} accepted · ${stats.abandoned} abandoned`}
              tone={stats.decided && stats.acceptedCount / stats.decided < 0.8 ? 'warn' : 'ok'}
            />
            <StatCard
              label="Escalated past L1"
              value={stats.acceptedCount ? `${Math.round((stats.escalatedBeyondL1 / stats.acceptedCount) * 100)}%` : '—'}
              sub={`${stats.escalatedBeyondL1} of ${stats.acceptedCount} needed L2+`}
              tone={stats.acceptedCount && stats.escalatedBeyondL1 / stats.acceptedCount > 0.3 ? 'warn' : 'ok'}
            />
            <StatCard
              label="Avg requester rating"
              value={stats.csatCount ? `${stats.csatAvg.toFixed(1)} / 5` : '—'}
              sub={stats.csatCount ? `${stats.csatCount} rated` : 'no ratings yet'}
            />
            <StatCard
              label="90th pct accept"
              value={fmtDuration(stats.p90Accept)}
              sub="9 in 10 accepted faster"
            />
          </div>

          <h3 style={{ marginTop: 28, marginBottom: 8 }}>By skill category</h3>
          {stats.bySkill.length === 0 ? (
            <div className="placeholder">No skill data in this period.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th style={tr}>Tickets</th>
                  <th style={tr}>Accepted</th>
                  <th style={tr}>Abandoned</th>
                  <th style={tr}>Median accept</th>
                </tr>
              </thead>
              <tbody>
                {stats.bySkill.map((s) => (
                  <tr key={s.skill}>
                    <td>{s.skill}</td>
                    <td style={tr}>{s.total}</td>
                    <td style={tr}>{s.accepted}</td>
                    <td style={tr}>
                      {s.abandoned > 0
                        ? <span style={{ color: '#dc2626' }}>{s.abandoned}</span>
                        : <span style={{ color: 'var(--muted)' }}>0</span>}
                    </td>
                    <td style={tr}>{fmtDuration(s.medianAccept)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}

function StatCard({ label, value, sub, tone }) {
  const accent = tone === 'warn' ? '#dc2626' : tone === 'ok' ? '#10b981' : 'var(--text, #0f172a)';
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, margin: '4px 0', color: accent }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

// ---- pure helpers ----

function computeStats(tickets, days) {
  const cutoff = days == null ? 0 : Date.now() - days * 24 * 60 * 60 * 1000;
  const inRange = tickets.filter((t) => {
    const ms = toMs(t.createdAt);
    return ms != null && ms >= cutoff;
  });

  const acceptTimes = [];
  let acceptedCount = 0, abandoned = 0, active = 0, escalatedBeyondL1 = 0;
  let csatSum = 0, csatCount = 0;
  const skillMap = new Map();

  for (const t of inRange) {
    const status = t.status || '';
    const isActive = status === 'NEW' || status === 'ROUTING' || status.startsWith('RINGING_');
    const acceptedMs = toMs(t.assignedTo?.acceptedAt);
    const isAccepted = acceptedMs != null;
    if (isActive) active++;
    if (status === 'ABANDONED') abandoned++;

    let tta = null;
    if (isAccepted) {
      acceptedCount++;
      const createdMs = toMs(t.createdAt);
      if (createdMs != null && acceptedMs >= createdMs) {
        tta = acceptedMs - createdMs;
        acceptTimes.push(tta);
      }
      if ((t.assignedTo?.level || 1) > 1) escalatedBeyondL1++;
    }

    const rating = t.feedback?.customer?.rating;
    if (typeof rating === 'number') { csatSum += rating; csatCount++; }

    const skill = t.skillName || 'Uncategorized';
    if (!skillMap.has(skill)) skillMap.set(skill, { skill, total: 0, accepted: 0, abandoned: 0, times: [] });
    const row = skillMap.get(skill);
    row.total++;
    if (isAccepted) row.accepted++;
    if (status === 'ABANDONED') row.abandoned++;
    if (tta != null) row.times.push(tta);
  }

  const bySkill = [...skillMap.values()]
    .map((r) => ({ ...r, medianAccept: median(r.times) }))
    .sort((a, b) => b.total - a.total);

  return {
    total: inRange.length,
    active,
    abandoned,
    acceptedCount,
    decided: acceptedCount + abandoned,
    escalatedBeyondL1,
    medianAccept: median(acceptTimes),
    p90Accept: percentile(acceptTimes, 90),
    csatAvg: csatCount ? csatSum / csatCount : 0,
    csatCount,
    bySkill,
  };
}

function toMs(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

function median(arr) {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function percentile(arr, p) {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const idx = Math.min(s.length - 1, Math.ceil((p / 100) * s.length) - 1);
  return s[Math.max(0, idx)];
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) return rem ? `${min}m ${rem}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr}h ${remMin}m` : `${hr}h`;
}

const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 14,
  marginTop: 16,
};

const cardStyle = {
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 'var(--radius, 10px)',
  padding: '14px 16px',
  background: 'var(--card, #fff)',
};

const tr = { textAlign: 'right' };
